import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, Typography, Grid, Paper, Fade, useTheme } from '@mui/material';
import AutoAwesomeTwoToneIcon from '@mui/icons-material/AutoAwesomeTwoTone';
import ViewSidebarTwoToneIcon from '@mui/icons-material/ViewSidebarTwoTone';
import { RoleName, Groups } from '../../../types/user';
import { adsHeight } from '../../layout/MainLayout';
import { useAppSelector } from '../../../redux/store';
import { useAuthenticatedUser } from '../../../hooks/useAuthenticatedUser';

const agents = [
	{
		id: 'daily-updates',
		name: 'Daily Updates',
		icon: <AutoAwesomeTwoToneIcon />,
		color: '#4285F4',
		roles: [
			RoleName.OrgnaisationAdmin,
			RoleName.HR,
			RoleName.Registrar,
			RoleName.AdmissionsOfficer,
			RoleName.Agent,
			RoleName.Verifier,
			RoleName.Onboarding,
			RoleName.Student,
			RoleName.Employee,
			RoleName.VerifierOnlyOrg
		],
		admin: true
	},
	{
		id: 'document-requests',
		name: 'Document Requests',
		icon: <ViewSidebarTwoToneIcon />,
		color: '#EA4335',
		roles: [RoleName.AdmissionsOfficer, RoleName.Onboarding, RoleName.Student],
		admin: false
	}
];

const MultiAgents = ({
	open,
	onClose,
	agentClick
}: {
	open: boolean;
	onClose: () => void;
	agentClick: (agentId: string) => void;
}) => {
	const [isMobile, setIsMobile] = useState(false);
	const theme = useTheme();

	const user = useAppSelector((state) => state.user.currentUser);
	const roles = user?.roles.map((role) => role.roleName);

	const { groups } = useAuthenticatedUser();

	useEffect(() => {
		const checkScreenSize = () => {
			setIsMobile(window.innerWidth <= 768);
		};

		checkScreenSize();
		window.addEventListener('resize', checkScreenSize);

		return () => {
			window.removeEventListener('resize', checkScreenSize);
		};
	}, []);

	const handleAgentClick = (agentId: string) => {
		agentClick(agentId);
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="xs"
			fullWidth
			disableScrollLock
			TransitionComponent={Fade}
			TransitionProps={{ timeout: 200 }}
			BackdropProps={{
				style: { backgroundColor: 'transparent' }
			}}
			sx={{
				'& .MuiDialog-container': {
					alignItems: 'flex-end',
					justifyContent: 'flex-end'
				},
				'& .MuiBackdrop-root': {
					backgroundColor: 'transparent'
				}
			}}
			PaperProps={{
				sx: {
					borderRadius: 8,
					height: 'auto',
					position: 'fixed',
					right: 10,
					left: 'auto',
					top: 'auto',
					bottom: isMobile ? adsHeight * 16 + 70 : 150, // Position above the buttons
					m: 0,
					width: isMobile ? 280 : 360,
					maxWidth: isMobile ? 280 : '100%',
					zIndex: 1500, // Higher than the buttons (1000, 1001)
					overflow: 'hidden',
					background: theme.palette.background.paper,
					boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 123, 255, 0.05)'
				}
			}}
		>
			<DialogTitle
				sx={{
					backgroundColor: 'var(--primary-1)',
					color: 'white',
					fontSize: '1.1rem',
					py: 1.5,
					textAlign: 'center',
					fontWeight: '600',
					borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
				}}
			>
				Personal Assistants
			</DialogTitle>

			<Box sx={{ p: 2, pb: 3 }}>
				<Typography
					variant="subtitle2"
					sx={{
						mb: 2,
						color: 'text.secondary',
						textAlign: 'center',
						fontSize: '0.85rem'
					}}
				>
					Select a personal assistant to help with your tasks
				</Typography>

				<Grid container spacing={2}>
					{agents
						.filter((agent) =>
							agent.roles.some((role) => {
								if (roles?.includes(role) || (groups?.includes(Groups.Admin) && agent.admin)) {
									return true;
								}
								return false;
							})
						)
						.map((agent) => (
							<Grid item xs={6} sm={4} key={agent.id}>
								<Paper
									elevation={0}
									onClick={() => handleAgentClick(agent.id)}
									sx={{
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										borderRadius: 2,
										py: 1,
										background: 'transparent',
										cursor: 'pointer',
										transition: 'all 0.2s ease',
										'&:hover': {
											backgroundColor: 'rgba(0, 0, 0, 0.04)',
											transform: 'translateY(-2px)'
										}
									}}
								>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											backgroundColor: `${agent.color}15`,
											color: agent.color,
											borderRadius: '12px',
											p: 1.5,
											mb: 1,
											width: 56,
											height: 56
										}}
									>
										{React.cloneElement(agent.icon, {
											sx: { fontSize: '1.8rem' }
										})}
									</Box>
									<Typography
										variant="caption"
										sx={{
											textAlign: 'center',
											fontWeight: 500,
											fontSize: '0.75rem',
											color: 'text.primary',
											mt: 0.5
										}}
									>
										{agent.name}
									</Typography>
								</Paper>
							</Grid>
						))}
					{agents.filter((agent) =>
						agent.roles.some((role) => {
							if (roles?.includes(role) || (groups?.includes(Groups.Admin) && agent.admin)) {
								return true;
							}
							return false;
						})
					).length === 0 && (
						<Grid item xs={12}>
							<Paper
								elevation={0}
								sx={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									justifyContent: 'center',
									borderRadius: 2,
									py: 3,
									px: 2,
									background: 'rgba(0, 0, 0, 0.02)',
									border: '1px dashed rgba(0, 0, 0, 0.1)',
									width: '100%'
								}}
							>
								<Typography
									variant="subtitle2"
									sx={{
										textAlign: 'center',
										fontSize: '0.9rem',
										color: 'text.secondary',
										fontWeight: 500
									}}
								>
									No personal assistants available
								</Typography>
							</Paper>
						</Grid>
					)}
				</Grid>
			</Box>
		</Dialog>
	);
};

export default MultiAgents;
